"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ServiceLocation } from "./ServiceLocationsList";
import { Loader2 } from "lucide-react";
import { GoogleMap, Polygon } from "@react-google-maps/api";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { grayMapStyle } from "@/utils/variables";
import axiosInstance from "@/utils/axiosInstance";

interface AreaLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: ServiceLocation | null;
  mode: "create" | "edit";
  onSuccess: () => void;
}

export function AreaLocationDialog({
  open,
  onOpenChange,
  location,
  mode,
  onSuccess,
}: AreaLocationDialogProps) {
  const [isCreating, setIsCreating] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const [formData, setFormData] = React.useState({
    name: "",
    type: "AREA" as const,
    coordinates: [] as Array<{ lat: number; lng: number }>,
    description: "",
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [polygonPath, setPolygonPath] = React.useState<google.maps.LatLng[]>(
    [],
  );
  const [isDrawing, setIsDrawing] = React.useState(false);

  const { isLoaded, loadError } = useGoogleMaps();
  const [mapCenter, setMapCenter] = React.useState({ lat: 41.3275, lng: 19.8189 });
  const [mapZoom, setMapZoom] = React.useState(13);
  const mapRef = React.useRef<google.maps.Map | null>(null);

  const isLoading = isCreating || isUpdating;

  // Function to focus map on polygon bounds
  const focusMapOnPolygon = React.useCallback((path: google.maps.LatLng[]) => {
    if (!mapRef.current || path.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    path.forEach(point => bounds.extend(point));
    
    mapRef.current.fitBounds(bounds);
    
    // Add some padding to the bounds
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    mapRef.current.fitBounds(bounds, padding);
  }, []);

  // Function to calculate polygon center for single point focus
  const calculatePolygonCenter = (coordinates: Array<{ lat: number; lng: number }>) => {
    if (coordinates.length === 0) return { lat: 41.3275, lng: 19.8189 };
    
    const latSum = coordinates.reduce((sum, coord) => sum + coord.lat, 0);
    const lngSum = coordinates.reduce((sum, coord) => sum + coord.lng, 0);
    
    return {
      lat: latSum / coordinates.length,
      lng: lngSum / coordinates.length
    };
  };

  // Reset form when location changes or dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === "edit" && location) {
        setFormData({
          name: location.name,
          type: "AREA",
          coordinates: location.coordinates,
          description: location.description || "",
        });
        // Convert coordinates to polygon path
        if (location.coordinates.length > 0) {
          const path = location.coordinates.map(
            (coord) => new google.maps.LatLng(coord.lat, coord.lng),
          );
          setPolygonPath(path);
          
          // Set map center to polygon center
          const center = calculatePolygonCenter(location.coordinates);
          setMapCenter(center);
          setMapZoom(13);
        }
      } else {
        setFormData({
          name: "",
          type: "AREA",
          coordinates: [],
          description: "",
        });
        setPolygonPath([]);
        // Reset to default center when creating new
        setMapCenter({ lat: 41.3275, lng: 19.8189 });
        setMapZoom(13);
      }
      setErrors({});
    }
  }, [open, mode, location]);

  // Focus map on polygon when it changes
  React.useEffect(() => {
    if (polygonPath.length > 2 && mapRef.current) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        focusMapOnPolygon(polygonPath);
      }, 100);
    }
  }, [polygonPath, focusMapOnPolygon]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Area name is required";
    }
    if (!formData.coordinates || formData.coordinates.length < 3) {
      newErrors.coordinates =
        "At least 3 points are required to define an area";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        name: formData.name.trim(),
        type: formData.type,
        coordinates: formData.coordinates,
        description: formData.description.trim() || undefined,
      };

      if (mode === "create") {
        setIsCreating(true);
        await axiosInstance.post("/route-areas", data);
        setIsCreating(false);
        toast("Area location created successfully", {
          description: "Your area location was added successfully!",
        });
      } else if (mode === "edit" && location) {
        // Use axios directly for PATCH request
        setIsUpdating(true);
        await axiosInstance.patch(`/route-areas/${location.id}`, data);
        setIsUpdating(false);
        toast("Area location updated successfully", {
          description: "Your area location was updated successfully!",
        });
      }
      onSuccess();
    } catch (error) {
      setIsCreating(false);
      setIsUpdating(false);
      toast(
        `Error ${mode === "create" ? "creating" : "updating"} area location`,
        {
          description:
            "Please try again or contact support if the problem persists.",
        },
      );
    }
  };

  const handleInputChange = (
    field: string,
    value: string | Array<{ lat: number; lng: number }>,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng && isDrawing) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newPoint = new google.maps.LatLng(lat, lng);

      const newPath = [...polygonPath, newPoint];
      setPolygonPath(newPath);

      const coordinates = newPath.map((point) => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
      handleInputChange("coordinates", coordinates);
    }
  };

  const startDrawing = () => {
    setIsDrawing(true);
    setPolygonPath([]);
    handleInputChange("coordinates", []);
  };

  const finishDrawing = () => {
    setIsDrawing(false);
  };

  const clearPolygon = () => {
    setPolygonPath([]);
    handleInputChange("coordinates", []);
    setIsDrawing(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[1200px] max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {mode === "create"
                ? "Create Area Location"
                : "Edit Area Location"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Draw an area on the map to create a new location area."
                : "Update the area location details."}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4 overflow-auto">
            <form
              id="area-location-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="name">Area Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter area name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coordinates">Area Coordinates</Label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={isDrawing ? "default" : "outline"}
                      size="sm"
                      onClick={startDrawing}
                      disabled={isDrawing}
                    >
                      {isDrawing ? "Drawing..." : "Start Drawing"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={finishDrawing}
                      disabled={!isDrawing}
                    >
                      Finish Drawing
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearPolygon}
                    >
                      Clear Area
                    </Button>
                  </div>

                  {isLoaded && !loadError && (
                    <div className="border rounded-md overflow-hidden">
                      <GoogleMap
                        center={mapCenter}
                        zoom={mapZoom}
                        mapContainerClassName="h-[500px] w-full"
                        onClick={handleMapClick}
                        onLoad={(map) => {
                          mapRef.current = map;
                        }}
                        options={{
                          styles: grayMapStyle,
                          fullscreenControl: false,
                          streetViewControl: false,
                          mapTypeControl: false,
                          gestureHandling: "greedy",
                        }}
                      >
                        {polygonPath.length > 0 && (
                          <Polygon
                            paths={polygonPath}
                            options={{
                              fillColor: "#3b82f6",
                              fillOpacity: 0.3,
                              strokeColor: "#1d4ed8",
                              strokeOpacity: 1,
                              strokeWeight: 2,
                            }}
                          />
                        )}
                      </GoogleMap>
                      <div className="p-2 bg-gray-50 text-sm text-gray-600">
                        {isDrawing ? (
                          <span className="text-blue-600 font-medium">
                            Click on the map to add points to your area (
                            {polygonPath.length} points)
                          </span>
                        ) : (
                          <span>
                            Click "Start Drawing" to begin creating your area
                            {polygonPath.length > 0 && (
                              <span className="ml-2 font-mono">
                                Area with {polygonPath.length} points
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {loadError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-600">
                        Error loading map: {loadError.message}
                      </p>
                    </div>
                  )}

                  {!isLoaded && (
                    <div className="p-4 bg-gray-50 border rounded-md">
                      <p className="text-gray-600">Loading map...</p>
                    </div>
                  )}
                </div>
                {errors.coordinates && (
                  <p className="text-sm text-red-500">{errors.coordinates}</p>
                )}
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="mt-4 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="area-location-form"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create Area" : "Update Area"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
